// src/pages/admin/Messages.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useRef } from "react";
import { adminApi } from "../../api/adminApi";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
  Alert,
  Dialog,
  DialogContent,
  Slider,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Button,
  Collapse,
} from "@mui/material";
import {
  Search,
  Refresh,
  Store,
  Person,
  ChatBubbleOutline,
  ArrowBack,
  PlayArrow,
  Pause,
  Close,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import axiosInstance from "../../api/axiosConfig";

// ============================================================
// UTILITY FUNCTION - ROBUST SANITIZER
// ============================================================
const getFullUrl = (url) => {
  if (!url) return null;
  
  let cleanUrl = String(url).trim();

  // Fix accidental leading slash before http(s)://
  if (cleanUrl.startsWith('/http://') || cleanUrl.startsWith('/https://')) {
    cleanUrl = cleanUrl.substring(1);
  }

  // Handle blob or data URLs directly
  if (cleanUrl.startsWith('blob:') || cleanUrl.startsWith('data:')) {
    return cleanUrl;
  }

  // If already a complete http/https URL, return it
  if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
    return cleanUrl;
  }

  const baseUrl = process.env.REACT_APP_API_URL || 'https://api.goscitech.com';
  
  // Clean /api/ prefix if present
  cleanUrl = cleanUrl.replace(/^\/?(api\/)+/, '');
  
  if (!cleanUrl.startsWith('/')) {
    cleanUrl = '/' + cleanUrl;
  }
  
  const fullUrl = `${baseUrl}${cleanUrl}`;
  return fullUrl;
};

// ============================================================
// VOICE PLAYER COMPONENT
// ============================================================
const VoicePlayer = ({ url, isMine, duration: initialDuration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  const fullUrl = getFullUrl(url);

  useEffect(() => {
    if (!fullUrl) {
      setError(true);
      setIsLoading(false);
      return;
    }

    if (fullUrl.startsWith('blob:')) {
      setIsLoading(false);
      setDuration(initialDuration || 0);
      return;
    }

    const audio = new Audio();
    audio.crossOrigin = "anonymous"; // Cross-origin streaming
    audioRef.current = audio;
    audio.preload = "metadata";

    const handleEnded = () => {
      setIsPlaying(false);
      clearInterval(intervalRef.current);
      setCurrentTime(0);
    };

    const handleError = (e) => {
      console.error('Audio loading error:', fullUrl, e);
      setIsPlaying(false);
      setIsLoading(false);
      setError(true);
      clearInterval(intervalRef.current);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setError(false);
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      } else {
        setDuration(initialDuration || 0);
      }
    };

    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    audio.src = fullUrl;
    audio.load();

    const timeoutId = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setDuration(initialDuration || 0);
        setError(false);
      }
    }, 5000);

    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('canplaythrough', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      clearInterval(intervalRef.current);
      clearTimeout(timeoutId);
      audio.pause();
      audio.src = '';
    };
  }, [fullUrl, initialDuration]);

  useEffect(() => {
    if (isPlaying && audioRef.current) {
      intervalRef.current = setInterval(() => {
        if (audioRef.current) {
          const time = audioRef.current.currentTime;
          if (!isNaN(time) && isFinite(time)) {
            setCurrentTime(time);
            
            const dur = audioRef.current.duration;
            if (dur && !isNaN(dur) && isFinite(dur) && time >= dur && dur > 0) {
              setIsPlaying(false);
              clearInterval(intervalRef.current);
            }
          }
        }
      }, 100);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || error) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      clearInterval(intervalRef.current);
    } else {
      if (audio.currentTime >= (audio.duration || 0)) {
        audio.currentTime = 0;
        setCurrentTime(0);
      }
      
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch((err) => {
            console.error('Play failed:', err);
            setIsPlaying(false);
            clearInterval(intervalRef.current);
            setError(true);
          });
      }
    }
  };

  const handleSliderChange = (_, value) => {
    const audio = audioRef.current;
    if (audio && isFinite(value) && !isNaN(value)) {
      const wasPlaying = isPlaying;
      if (wasPlaying) {
        audio.pause();
        clearInterval(intervalRef.current);
      }
      
      audio.currentTime = value;
      setCurrentTime(value);
      
      if (wasPlaying) {
        audio.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(() => {
            setIsPlaying(false);
          });
      }
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds) || seconds === Infinity) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (error) {
    return (
      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'error.light', borderRadius: 2 }}>
        <Typography variant="caption" color="error" display="block">
          ⚠️ Audio unavailable
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
          {url ? 'File may be corrupted or missing' : 'No audio file'}
        </Typography>
      </Box>
    );
  }

  const displayDuration = duration || initialDuration || 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: isMine ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.04)",
        borderRadius: 2,
        p: 1.5,
        minWidth: "200px",
        mt: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ width: "100%" }}
      >
        <IconButton
          size="small"
          sx={{ color: isMine ? "white" : "primary.main" }}
          onClick={togglePlayPause}
          disabled={isLoading || error}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: isMine ? "white" : "primary.main" }} />
          ) : isPlaying ? (
            <Pause />
          ) : (
            <PlayArrow />
          )}
        </IconButton>

        <Slider
          size="small"
          value={currentTime}
          min={0}
          max={displayDuration || 1}
          onChange={handleSliderChange}
          disabled={isLoading || error}
          sx={{
            flex: 1,
            color: isMine ? "white" : "primary.main",
            "& .MuiSlider-thumb": {
              width: 12,
              height: 12,
            },
          }}
        />
      </Stack>

      <Typography
        variant="caption"
        sx={{
          alignSelf: "flex-end",
          mt: 0.5,
          color: isMine ? "rgba(255,255,255,0.7)" : "text.secondary",
          fontFamily: "monospace",
          fontSize: "0.75rem",
        }}
      >
        {isPlaying || currentTime > 0
          ? formatDuration(currentTime) + " / " + formatDuration(displayDuration)
          : formatDuration(displayDuration)}
      </Typography>
    </Box>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const AdminMessages = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomMessages, setRoomMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState(null);

  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getAdminMessages();

      let extractedData = [];
      if (response && response.data && response.data.data) {
        extractedData = response.data.data;
      } else if (response && response.data) {
        extractedData = response.data;
      } else if (Array.isArray(response)) {
        extractedData = response;
      }

      setData(extractedData);
    } catch (err) {
      setError("Failed to load messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSelectRoom = async (room) => {
    setSelectedRoom(room);
    setLoadingMessages(true);
    try {
      const response = await axiosInstance.get(
        `/chat/rooms/${room.id}/messages`,
      );
      const messages = (response.data.messages || []).map(msg => {
        if (msg.attachment && msg.attachment.url) {
          return {
            ...msg,
            attachment: {
              ...msg.attachment,
              url: getFullUrl(msg.attachment.url)
            }
          };
        }
        return msg;
      });
      setRoomMessages(messages);
    } catch (err) {
      setRoomMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleBackToCustomers = () => {
    setSelectedRoom(null);
    setRoomMessages([]);
  };

  const handleBackToVendors = () => {
    setSelectedVendor(null);
    setSelectedRoom(null);
    setRoomMessages([]);
  };

  const toggleVendorExpand = (vendorId) => {
    setExpandedVendor(expandedVendor === vendorId ? null : vendorId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ============================================================
  // TIER 1: Vendor List
  // ============================================================
  if (!selectedVendor) {
    const filteredVendors = data.filter((v) =>
      v.vendorInfo?.storeName?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return (
      <Box sx={{ pb: { xs: 8, sm: 10 } }}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
                Platform Messages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a vendor to view their customer chats
              </Typography>
            </Box>
            <IconButton onClick={fetchData} size={isMobile ? 'small' : 'medium'}>
              <Refresh />
            </IconButton>
          </Stack>
        </Paper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
          <TextField
            fullWidth
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : filteredVendors.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                No active conversations found.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ p: 1.5 }}>
              {filteredVendors.map((v) => {
                const isExpanded = expandedVendor === v.vendorId;
                const logoUrl = v.vendorInfo?.storeLogo ? getFullUrl(v.vendorInfo.storeLogo) : "";
                
                return (
                  <Card key={v.vendorId} sx={{ mb: 1.5, borderRadius: 2 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack 
                          direction="row" 
                          alignItems="center" 
                          justifyContent="space-between"
                        >
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar
                              src={logoUrl}
                              sx={{ bgcolor: "primary.main", width: 40, height: 40 }}
                            >
                              <Store />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {v.vendorInfo?.storeName || "Unknown Store"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {v.totalRooms || 0} active customer chats
                              </Typography>
                            </Box>
                          </Stack>
                          <IconButton 
                            size="small" 
                            onClick={() => toggleVendorExpand(v.vendorId)}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Stack>
                        
                        <Collapse in={isExpanded}>
                          <Divider sx={{ my: 1 }} />
                          <Stack spacing={1}>
                            {v.rooms?.slice(0, 3).map((room) => (
                              <Button
                                key={room.id}
                                variant="outlined"
                                size="small"
                                fullWidth
                                startIcon={<ChatBubbleOutline />}
                                onClick={() => {
                                  setSelectedVendor(v);
                                  handleSelectRoom(room);
                                }}
                                sx={{ 
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {room.Buyer?.name || 'Unknown Customer'}
                              </Button>
                            ))}
                            {v.rooms?.length > 3 && (
                              <Button
                                size="small"
                                fullWidth
                                onClick={() => setSelectedVendor(v)}
                                sx={{ textTransform: 'none' }}
                              >
                                View all {v.rooms.length} chats →
                              </Button>
                            )}
                          </Stack>
                        </Collapse>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <List>
              {filteredVendors.map((v) => {
                const logoUrl = v.vendorInfo?.storeLogo ? getFullUrl(v.vendorInfo.storeLogo) : "";
                
                return (
                  <React.Fragment key={v.vendorId}>
                    <ListItem 
                      button 
                      onClick={() => setSelectedVendor(v)}
                      sx={{ py: 1.5 }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={logoUrl}
                          sx={{ bgcolor: "primary.main" }}
                        >
                          <Store />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography fontWeight="bold">
                            {v.vendorInfo?.storeName || "Unknown Store"}
                          </Typography>
                        }
                        secondary={`${v.totalRooms || 0} active customer chats`}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Paper>
      </Box>
    );
  }

  // ============================================================
  // TIER 2: Customer List (Rooms)
  // ============================================================
  if (!selectedRoom) {
    const logoUrl = selectedVendor.vendorInfo?.storeLogo ? getFullUrl(selectedVendor.vendorInfo.storeLogo) : "";
    
    return (
      <Box sx={{ pb: { xs: 8, sm: 10 } }}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton onClick={handleBackToVendors} size={isMobile ? 'small' : 'medium'}>
                <ArrowBack />
              </IconButton>
              <Avatar
                src={logoUrl}
                sx={{ bgcolor: "primary.main" }}
              >
                <Store />
              </Avatar>
            </Stack>
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
                {selectedVendor.vendorInfo?.storeName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a customer to view the full conversation
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
          {!selectedVendor.rooms || selectedVendor.rooms.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                No customer chats for this vendor.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ p: 1.5 }}>
              {selectedVendor.rooms.map((room) => (
                <Card key={room.id} sx={{ mb: 1.5, borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: "secondary.main", width: 40, height: 40 }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {room.Buyer?.name || "Unknown Customer"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last active: {formatDate(room.updatedAt)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ChatBubbleOutline />}
                        onClick={() => handleSelectRoom(room)}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        View
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <List>
              {selectedVendor.rooms.map((room) => (
                <React.Fragment key={room.id}>
                  <ListItem 
                    button 
                    onClick={() => handleSelectRoom(room)}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "secondary.main" }}>
                        <Person />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography fontWeight="bold">
                          {room.Buyer?.name || "Unknown Customer"}
                        </Typography>
                      }
                      secondary={`Last active: ${formatDate(room.updatedAt)}`}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    );
  }

  // ============================================================
  // TIER 3: Full Chat View (For Admin Monitoring)
  // ============================================================
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { xs: "calc(100vh - 160px)", sm: "calc(100vh - 140px)" },
        pb: { xs: 8, sm: 10 },
      }}
    >
      {/* Chat Header */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          spacing={isMobile ? 1 : 2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={handleBackToCustomers} size={isMobile ? 'small' : 'medium'}>
              <ArrowBack />
            </IconButton>
            <Avatar sx={{ bgcolor: "secondary.main" }}>
              <Person />
            </Avatar>
          </Stack>
          <Box>
            <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
              {selectedRoom.Buyer?.name || "Customer"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Chatting with {selectedVendor.vendorInfo?.storeName}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Messages Area */}
      <Paper
        sx={{
          flex: 1,
          overflow: "auto",
          p: { xs: 1.5, sm: 2 },
          mb: 2,
          borderRadius: 3,
          bgcolor: "#fafafa",
        }}
      >
        {loadingMessages ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : roomMessages.length === 0 ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Typography color="text.secondary">
              No messages in this conversation.
            </Typography>
          </Box>
        ) : (
          roomMessages.map((msg) => {
            const isVendor = msg.senderType === "vendor";
            const attachmentUrl = msg.attachment?.url ? getFullUrl(msg.attachment.url) : null;
            
            return (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent: isVendor ? "flex-end" : "flex-start",
                  mb: 2,
                }}
              >
                <Paper
                  sx={{
                    p: 1.5,
                    maxWidth: { xs: "85%", sm: "70%" },
                    bgcolor: isVendor ? "primary.main" : "white",
                    color: isVendor ? "white" : "text.primary",
                    borderRadius: 2,
                  }}
                >
                  {msg.attachment?.type === "image" && attachmentUrl && (
                    <Box
                      sx={{ mb: 1, cursor: "pointer" }}
                      onClick={() => {
                        setSelectedImage(attachmentUrl);
                        setOpenImage(true);
                      }}
                    >
                      <img
                        src={attachmentUrl}
                        alt="Attachment"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "150px",
                          borderRadius: "8px",
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = 
                            '<span style="color: ' + (isVendor ? 'white' : 'text.secondary') + ';">Image unavailable</span>';
                        }}
                      />
                    </Box>
                  )}
                  {msg.attachment?.type === "file" && (
                    <Box
                      sx={{
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        bgcolor: isVendor
                          ? "rgba(255,255,255,0.15)"
                          : "rgba(0,0,0,0.05)",
                        borderRadius: 2,
                        p: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {msg.attachment.fileName || "Attachment"}
                      </Typography>
                    </Box>
                  )}
                  {msg.attachment?.type === "voice" && (
                    <VoicePlayer
                      url={msg.attachment.url}
                      isMine={isVendor}
                      duration={msg.attachment.duration}
                    />
                  )}
                  {msg.message && (
                    <Typography variant="body2">{msg.message}</Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "right",
                      mt: 0.5,
                      color: isVendor
                        ? "rgba(255,255,255,0.7)"
                        : "text.secondary",
                    }}
                  >
                    {formatDate(msg.createdAt)}
                  </Typography>
                </Paper>
              </Box>
            );
          })
        )}
      </Paper>

      {/* Footer Note */}
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          bgcolor: "warning.light",
          textAlign: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          👁️ Admin Monitoring Mode - You are viewing this conversation but cannot reply.
        </Typography>
      </Paper>

      {/* Image Dialog */}
      <Dialog
        open={openImage}
        onClose={() => setOpenImage(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { bgcolor: "black", position: 'relative' }
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: { xs: "300px", sm: "400px" },
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setOpenImage(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "white",
              bgcolor: "rgba(0,0,0,0.5)",
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <Close />
          </IconButton>
          <img
            src={selectedImage}
            alt="Full View"
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = 
                '<Typography color="white" sx={{ p: 4 }}>Image failed to load</Typography>';
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminMessages;